import React from 'react';

import { ICartEntry } from 'features/cart/types';
import { calculateEntryTotalPrice } from 'features/cart/utils';
import { StyleSheet } from 'react-native';

import Currency from 'components/currency';
import { Box, Typography } from 'components/ui';
import { getDesignToken, spacing } from 'styles/constants';

const resolveEntryChildren = (
  entry: ICartEntry,
  currencyFormatter: Function
): React.ReactNode[] => {
  if (!entry.children?.length) {
    return [];
  }

  return entry.children.flatMap((child, index) => {
    if (child.children?.length) {
      return resolveEntryChildren(child, currencyFormatter);
    }

    return (
      <Typography variant="copyTwo" style={styles.entryDescriptionRow} key={`${child.id}-${index}`}>
        {child.name}
        {child.price?.amount != null ? ` - ${currencyFormatter(child.price.amount)}` : ''}
      </Typography>
    );
  });
};

const MinimalCartEntry = ({
  entry,
  currencyFormatter,
}: {
  entry: ICartEntry;
  currencyFormatter: Function;
}) => {
  const quantity = entry.quantity || 1;
  const amount = calculateEntryTotalPrice(entry);
  const shouldShowPrice = entry.price !== null;
  const cartEntryDescription = resolveEntryChildren(entry, currencyFormatter);

  return (
    <Box style={styles.item} accessible focusable>
      <Box style={styles.itemHeader}>
        <Typography variant="headerFour" style={styles.itemName}>
          {quantity > 1 && `${quantity}x `}
          {entry.name}
        </Typography>
        <Box style={styles.itemPrice}>
          {shouldShowPrice && (
            <Currency
              amount={amount}
              textProps={{
                ...(amount === 0 && { color: getDesignToken('icon-default') }),
                variant: 'headerFour',
                style: { margin: 0 },
                textAlign: 'right',
              }}
            />
          )}
        </Box>
      </Box>

      {cartEntryDescription}
    </Box>
  );
};

export default MinimalCartEntry;

const styles = StyleSheet.create({
  entryDescriptionRow: {
    lineHeight: 18,
    margin: 0,
    width: '100%',
  },
  item: {
    alignItems: 'baseline',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: spacing.$1,
    padding: 0,
  },
  itemHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.$1,
    width: '100%',
  },
  itemName: {
    flexBasis: '80%',
    flexShrink: 1,
    margin: 0,
  },
  itemPrice: {
    alignItems: 'flex-end',
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'center',
    width: '16.666%',
  },
});
